
class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.isOpen = false;
    this.lastSearchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    const form = this.querySelector('form.search');
    form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('input', debounce((event) => {
      this.onChange(event);
    }, 120).bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFocus() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) return;

    if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allElements = this.querySelectorAll('li');
    let activeElement = this.querySelector('li');

    if (moveUp && !selectedElement) return;

    if (this.statusElement) this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElement = selectedElement.nextElementSibling || allElements[0];
    } else if (moveUp) {
      activeElement = selectedElement.previousElementSibling || allElements[allElements.length - 1];
    }

    if (activeElement === selectedElement) return;

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.setLiveRegionText(activeElement.textContent);
    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedProduct = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button');
    if (selectedProduct) selectedProduct.click();
  }

  expandQuery(searchTerm) {
    const lower = searchTerm.toLowerCase();
    const synonyms = (window.FloraFuzzy && window.FloraFuzzy.synonyms) || {};
    const expansions = new Set([searchTerm]);
    Object.keys(synonyms).forEach((canonical) => {
      const variants = synonyms[canonical];
      if (canonical.toLowerCase() === lower || variants.some((v) => v.toLowerCase() === lower)) {
        expansions.add(canonical);
        variants.forEach((v) => expansions.add(v));
      }
    });
    if (expansions.size === 1) return searchTerm;
    return Array.from(expansions).slice(0, 6).join(' OR ');
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(/\s+/g, '-').toLowerCase();
    this.lastSearchTerm = searchTerm;
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey], searchTerm);
      return;
    }

    const expandedTerm = this.expandQuery(searchTerm);
    const fields = 'title,product_type,vendor,variants.title,tag,body';
    const url = `${routes.predictive_search_url}`
      + `?q=${encodeURIComponent(expandedTerm)}`
      + `&resources%5Btype%5D=product`
      + `&resources%5Blimit%5D=6`
      + `&resources%5Boptions%5D%5Bfields%5D=${encodeURIComponent(fields)}`
      + `&resources%5Boptions%5D%5Bunavailable_products%5D=last`
      + `&section_id=predictive-search`;

    fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error(response.status);
        return response.text();
      })
      .then((text) => {
        const dom = new DOMParser().parseFromString(text, 'text/html');
        const sectionEl = dom.querySelector('#shopify-section-predictive-search');
        const resultsMarkup = sectionEl ? sectionEl.innerHTML : '';
        this.cachedResults[queryKey] = resultsMarkup;
        this.renderSearchResults(resultsMarkup, searchTerm);
      })
      .catch((error) => {
        // Non-fatal: try fuzzy fallback
        this.tryFuzzyFallback(searchTerm, '');
        // eslint-disable-next-line no-console
        console.warn('Predictive search request failed:', error);
      });
  }

  countProductHits(markup) {
    if (!markup) return 0;
    const tmp = document.createElement('div');
    tmp.innerHTML = markup;
    return tmp.querySelectorAll('li.predictive-search__list-item[data-product-id]').length;
  }

  tryFuzzyFallback(searchTerm, originalMarkup) {
    if (!window.FloraFuzzy || typeof window.FloraFuzzy.search !== 'function') {
      this.predictiveSearchResults.innerHTML = originalMarkup
        || `<div class="predictive-search__empty p-3 text-center" style="opacity:0.7;">No matches for &ldquo;${searchTerm.replace(/[<>&]/g, '')}&rdquo;</div>`;
      this.setAttribute('results', true);
      this.setLiveRegionResultsSafe();
      this.open();
      return;
    }
    window.FloraFuzzy.search(searchTerm, 6).then((fuzzyHits) => {
      if (!fuzzyHits || fuzzyHits.length === 0) {
        this.predictiveSearchResults.innerHTML = originalMarkup
          || `<div class="predictive-search__empty p-3 text-center" style="opacity:0.7;">No matches for &ldquo;${searchTerm.replace(/[<>&]/g, '')}&rdquo;</div>`;
        this.setAttribute('results', true);
        this.setLiveRegionResultsSafe();
        this.open();
        return;
      }
      const fuzzyHTML = window.FloraFuzzy.renderResults(fuzzyHits, searchTerm);
      this.predictiveSearchResults.innerHTML = `<div id="predictive-search-results">${fuzzyHTML}</div>`;
      this.setAttribute('results', true);
      this.setLiveRegionResultsSafe();
      this.open();
    });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup, searchTerm) {
    const hitCount = this.countProductHits(resultsMarkup);
    if (hitCount === 0 && searchTerm) {
      this.tryFuzzyFallback(searchTerm, resultsMarkup);
      return;
    }
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);
    this.setLiveRegionResultsSafe();
    this.open();
  }

  setLiveRegionResultsSafe() {
    this.removeAttribute('loading');
    const countEl = this.querySelector('[data-predictive-search-live-region-count-value]');
    if (countEl) this.setLiveRegionText(countEl.textContent);
  }

  getResultsMaxHeight() {
    const headerEl = document.querySelector('.section-header');
    const top = headerEl ? headerEl.getBoundingClientRect().bottom : 80;
    this.resultsMaxHeight = window.innerHeight - top;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }

    const selected = this.querySelector('[aria-selected="true"]');
    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');

    this.isOpen = false;
  }
}

customElements.define('predictive-search', PredictiveSearch);
